import React, { useState, useEffect } from 'react';
import { consultantsAPI, masterSettingsAPI } from '@/api/api';
import { Edit, DollarSign, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const EditConsultant = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    course_payout_settings: []  // NEW: Fixed payout per course
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchCourses();
    fetchConsultantData();
  }, [id]);

  const fetchCourses = async () => {
    try {
      const response = await masterSettingsAPI.getCourses();
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchConsultantData = async () => {
    try {
      setLoadingData(true);
      const response = await consultantsAPI.getConsultant(id);
      const consultant = response.data;
      
      console.log('Fetched consultant data:', consultant);
      
      setFormData({
        name: consultant.name || '',
        email: consultant.email || '',
        phone: consultant.phone || '',
        address: consultant.address || '',
        course_payout_settings: consultant.course_payout_settings || []
      });
    } catch (error) {
      console.error('Error fetching consultant data:', error);
      setError('Failed to load consultant data. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const addCoursePayout = () => {
    setFormData({
      ...formData,
      course_payout_settings: [
        ...formData.course_payout_settings,
        { 
          course_id: '', 
          course_name: '',
          regular_payout: 0,
          lateral_payout: 0
        }
      ]
    });
  };

  const removeCoursePayout = (index) => {
    const updated = formData.course_payout_settings.filter((_, i) => i !== index);
    setFormData({ ...formData, course_payout_settings: updated });
  };

  const updateCoursePayout = (index, field, value) => {
    const updated = [...formData.course_payout_settings];
    
    if (field === 'course_id') {
      const selectedCourse = courses.find(c => c.id === value);
      updated[index].course_id = value;
      updated[index].course_name = selectedCourse?.name || '';
    } else {
      updated[index][field] = value;
    }
    
    setFormData({ ...formData, course_payout_settings: updated });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    formData.course_payout_settings.forEach((setting, index) => {
      if (!setting.course_id) {
        errors[`course_${index}`] = 'Please select a course';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        commission_type: 'fixed',
        commission_rate: 0,
        course_commissions: [],
        course_payout_settings: formData.course_payout_settings.map(setting => ({
          course_id: setting.course_id,
          course_name: setting.course_name,
          regular_payout: parseFloat(setting.regular_payout) || 0,
          lateral_payout: parseFloat(setting.lateral_payout) || 0
        }))
      };

      console.log('Updating consultant:', submitData);
      const response = await consultantsAPI.updateConsultant(id, submitData);
      console.log('Consultant updated successfully:', response.data);
      
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/consultants/list');
      }, 2000);
      
    } catch (error) {
      console.error('Error updating consultant:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update consultant';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading consultant data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Consultant</h1>
              <p className="text-gray-600 mt-1">Update consultant information and payout settings</p>
            </div>
            <button 
              onClick={() => navigate('/consultants/list')}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              ← Back to List
            </button>
          </div>
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 mb-6">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-green-800 font-medium">Consultant updated successfully! Redirecting...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 mb-6">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Edit className="h-5 w-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    formErrors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {formErrors.name && <p className="text-red-600 text-xs mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    formErrors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {formErrors.email && <p className="text-red-600 text-xs mt-1">{formErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    formErrors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {formErrors.phone && <p className="text-red-600 text-xs mt-1">{formErrors.phone}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address *
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    formErrors.address ? 'border-red-300' : 'border-gray-300'
                  }`}
                  rows="2"
                  required
                />
                {formErrors.address && <p className="text-red-600 text-xs mt-1">{formErrors.address}</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold text-gray-900">Fixed Payout Settings</h2>
              </div>
              <button
                type="button"
                onClick={addCoursePayout}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                + Add Course
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>💡 How it works:</strong> Set a fixed amount the consultant will receive for each student admission.
              </p>
            </div>

            {formData.course_payout_settings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No payout settings configured yet.</p>
                <p className="text-sm mt-1">Click "Add Course" to set fixed payouts.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.course_payout_settings.map((setting, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Course #{index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeCoursePayout(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Course *
                        </label>
                        <select
                          value={setting.course_id}
                          onChange={(e) => updateCoursePayout(index, 'course_id', e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                            formErrors[`course_${index}`] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          required
                        >
                          <option value="">-- Select Course --</option>
                          {courses.map(course => (
                            <option key={course.id} value={course.id}>
                              {course.name} ({course.code}) {course.lateral_entry_allowed ? '- Lateral Entry Allowed' : ''}
                            </option>
                          ))}
                        </select>
                        {formErrors[`course_${index}`] && (
                          <p className="text-red-600 text-xs mt-1">{formErrors[`course_${index}`]}</p>
                        )}
                        {(() => {
                          const selectedCourse = courses.find(c => c.id === setting.course_id);
                          if (selectedCourse?.lateral_entry_allowed) {
                            return (
                              <span className="inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                Lateral Entry Allowed (Semester {selectedCourse?.lateral_entry_semester || 3})
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>

                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <label className="block text-sm font-medium text-blue-800 mb-1">
                          Regular Entry Payout (₹)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="100"
                          value={setting.regular_payout}
                          onChange={(e) => updateCoursePayout(index, 'regular_payout', e.target.value)}
                          className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                        <p className="text-xs text-blue-600 mt-1">For students joining from 1st Semester</p>
                      </div>

                      {(() => {
                        const selectedCourse = courses.find(c => c.id === setting.course_id);
                        if (selectedCourse?.lateral_entry_allowed) {
                          return (
                            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                              <label className="block text-sm font-medium text-orange-800 mb-1">
                                Lateral Entry Payout (₹)
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="100"
                                value={setting.lateral_payout}
                                onChange={(e) => updateCoursePayout(index, 'lateral_payout', e.target.value)}
                                className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                placeholder="0"
                              />
                              <p className="text-xs text-orange-600 mt-1">For students joining from {selectedCourse?.lateral_entry_semester || 3}rd Semester</p>
                            </div>
                          );
                        } else if (setting.course_id) {
                          return (
                            <div className="flex items-center justify-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                              <p className="text-sm text-gray-500">Lateral Entry not available for this course</p>
                            </div>
                          );
                        } else {
                          return (
                            <div className="flex items-center justify-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                              <p className="text-sm text-gray-400">Please select a course</p>
                            </div>
                          );
                        }
                      })()}

                      <div className="flex items-center justify-center">
                        <div className="text-center bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-600 mb-1">Total Potential</p>
                          <p className="text-lg font-bold text-green-600">
                            ₹{(parseFloat(setting.regular_payout) || 0) + (parseFloat(setting.lateral_payout) || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => navigate('/consultants/list')}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <Edit className="h-4 w-4" />
                  <span>Update Consultant</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditConsultant;
